import DeleteIcon from "@mui/icons-material/Delete";
import {
    Button,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS, pl } from "date-fns/locale";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    StyledTableCell,
    StyledTableHead,
} from "../../../../../components/table/BaseTable/BaseTable";
import { isReservationPayment, isFinalSettlementPayment, useMilestones } from "../../../../../services/agreementsService";
import {
    clamp,
    formatDecimalNumber,
    getDiscountedPricesForProduct,
    useLanguage,
} from "../../../../../utils";
import { isSinglePaymentValid } from "../../useIsAgreementFormDataValid";
import { calculateTotalPercentagePerProduct, calculateTotalPricePerProduct } from "../../utils";
import { NO_BORDER_TEXT_FILED_STYLE, NUMERIC_FIELDS_DISABLE_ARROW_STYLE } from "../../../../../helpers/styles";
import { roundToDecimalPlaces } from "../../../../../helpers/methods";

export const PaymentCard = (props) => {
    const {
        idx,
        payment,
        setPayment,
        onDelete,
        investmentId,
        products,
        productDiscounts,
        allPayments,
        readonly = false,
    } = props;
    const { t } = useTranslation();
    const language = useLanguage();

    const [globalPercentage, setGlobalPercentage] = useState(null);

    const [dueDateType, setDueDateType] = useState(
        payment.agrepay_milestone != null || payment.agrepay_payment_shift != null
            ? "event"
            : "date",
    );

    useEffect(() => {
        setDueDateType(
            payment.agrepay_milestone != null || payment.agrepay_payment_shift != null
                ? "event"
                : "date",
        );
    }, [payment]);

    const { data: milestones } = useMilestones(investmentId);
    const isLoading = !milestones;

    const isThisReservationPayment = isReservationPayment(payment);
    const isThisFinalSettlementPayment = isFinalSettlementPayment(payment);
    const title = isThisFinalSettlementPayment ? 
        t("agreements.PaymentCard.finalSettlementPayment") :
            isThisReservationPayment ? 
            t("agreements.PaymentCard.reservationPayment") : 
            `${t("agreements.PaymentCard.tranche")} ${idx}`;

    const handleSetDueDate = useCallback(
        (newDate) =>
            setPayment({
                ...payment,
                agrepay_due_date: newDate,
                agrepay_milestone: null,
                agrepay_payment_shift: null,
                agrepay_plan_date: null,
            }),
        [payment, setPayment],
    );

    const handleSetMilestone = useCallback(
        (milestoneId) => {
            const selectedMilestone = milestones?.find((m) => m.id === milestoneId);
            setPayment({
                ...payment,
                agrepay_milestone: milestoneId,
                agrepay_due_date: null,
                agrepay_plan_date: selectedMilestone?.miledate_start || null,
            });
        },
        [payment, setPayment, milestones],
    );

    const handleSetPaymentShift = useCallback(
        (newShift) =>
            setPayment({
                ...payment,
                agrepay_payment_shift: newShift,
                agrepay_due_date: null,
            }),
        [payment, setPayment],
    );

    const productsMap = useMemo(
        () =>
            products.reduce((map, product) => {
                map[product.id] = product;
                return map;
            }, {}),
        [products],
    );

    const handleSetPercentage = useCallback(
        (productId, percentage) => {
            const clampedPercentage = clamp(percentage, isFinalSettlementPayment ? -100 : 0, 100);
            setGlobalPercentage(null);
            setPayment({
                ...payment,
                per_product_percentages: payment.per_product_percentages.map((pp) =>
                    pp.product_id === productId ? { ...pp, percentage: clampedPercentage } : pp,
                ),
            });
        },
        [payment, setPayment, setGlobalPercentage, isFinalSettlementPayment],
    );

    const handleSetGlobalPercentage = useCallback(
        (percentage) => {
            const clampedPercentage = Number(formatDecimalNumber(clamp(percentage, isFinalSettlementPayment ? -100 : 0, 100)))
            setGlobalPercentage(clampedPercentage);
            const newPerProductPercentages = payment.per_product_percentages.map((pp) => ({
                ...pp,
                percentage: clampedPercentage,
            }));
            setPayment({
                ...payment,
                per_product_percentages: newPerProductPercentages,
            });
        },
        [payment, setPayment, setGlobalPercentage, isFinalSettlementPayment],
    );

    const handleSetGrossAmount = useCallback(
        (productId, newAmount) => {
            const product = productsMap[productId];
            if (!product) {
                return;
            }
            const discount = productDiscounts?.[productId] ?? 0;
            const productPricing = getDiscountedPricesForProduct(product, discount);
            const grossPrice = productPricing.gross;
            const roundedAmount = Math.round(newAmount * 100) / 100;
            const effectiveAmount = clamp(roundedAmount, isThisFinalSettlementPayment ? -1*grossPrice : 0, grossPrice);
            const newPercentage = (effectiveAmount / grossPrice) * 100;
            handleSetPercentage(productId, newPercentage);
        },
        [productsMap, productDiscounts, handleSetPercentage, isThisFinalSettlementPayment],
    );

    const handleSetRest = useCallback(
        (productId, currentPercentage) => {
            const product = productsMap[productId];
            if (!product) {
                return
            }
            const discount = productDiscounts?.[productId] ?? 0;
            const productPricing = getDiscountedPricesForProduct(product, discount);
            const grossPrice = productPricing.gross;

            const productPrices = calculateTotalPricePerProduct(allPayments, products, productDiscounts);
            const totalProductPrice = productPrices[productId] || 0;
            const currentPrice = roundToDecimalPlaces(grossPrice * currentPercentage / 100, 2)
            
            const remainProductPrice  = roundToDecimalPlaces(grossPrice - totalProductPrice + currentPrice, 2)
            const effectiveAmount = clamp(remainProductPrice, 0, grossPrice);
            const newPercentage = (effectiveAmount / grossPrice) * 100;

            handleSetPercentage(productId, newPercentage);
        },
        [allPayments, handleSetPercentage, productsMap, products, productDiscounts],
    );

    // TODO Old solution
    // const handleSetRest = useCallback(
    //     (productId, currentPercentage) => {
    //         const productPercentages = calculateTotalPercentagePerProduct(allPayments);
    //         const totalProductPercentage = productPercentages[productId] || 0;
    //         const remainingPercentage = 100 - totalProductPercentage + currentPercentage;
    //         handleSetPercentage(productId, remainingPercentage);
    //     },
    //     [allPayments, handleSetPercentage],
    // );

    // TODO Change here
    // const handleSetRestForAll = useCallback(() => {
    //     const productPercentages = calculateTotalPercentagePerProduct(allPayments);
    //     const newPerProductPercentages = payment.per_product_percentages.map((pp) => {
    //         const totalProductPercentage = productPercentages[pp.product_id] || 0;
    //         const remainingPercentage = 100 - totalProductPercentage + pp.percentage;
    //         return {
    //             ...pp,
    //             percentage: remainingPercentage,
    //         };
    //     });
    //     const newPerProductPercentagesSet = new Set(newPerProductPercentages.map(productObj=>productObj.percentage))
    //     if (newPerProductPercentagesSet.size === 1) {
    //         setGlobalPercentage([...newPerProductPercentagesSet][0])
    //     } else {
    //          setGlobalPercentage(null);
    //     }
    //     setPayment({
    //         ...payment,
    //         per_product_percentages: newPerProductPercentages,
    //     });
    // }, [payment, setPayment, allPayments, setGlobalPercentage]);

    const handleSetRestForAll = useCallback(() => {
        const productPercentages = calculateTotalPercentagePerProduct(allPayments);
        const newPerProductPercentages = payment.per_product_percentages.map((pp) => {
            // product_id: 'a799f23e-9c9d-4416-ac20-30fcabde31bd', percentage: 49.9999989641535
            const product = productsMap[pp.product_id];
            if (!product) {
                    return {
                    ...pp,
                    percentage: 0,
                };
            }
            const discount = productDiscounts?.[pp.product_id] ?? 0;
            const productPricing = getDiscountedPricesForProduct(product, discount);
            const grossPrice = productPricing.gross;

            const productPrices = calculateTotalPricePerProduct(allPayments, products, productDiscounts);
            const totalProductPrice = productPrices[pp.product_id] || 0;
            const currentPrice = roundToDecimalPlaces(grossPrice * pp.percentage / 100, 2)
            
            const remainProductPrice  = roundToDecimalPlaces(grossPrice - totalProductPrice + currentPrice, 2)
            const effectiveAmount = clamp(remainProductPrice, 0, grossPrice);
            const newPercentage = (effectiveAmount / grossPrice) * 100;
            
            return {
                ...pp,
                percentage: newPercentage,
            };
        });


        const newPerProductPercentagesSet = new Set(newPerProductPercentages.map(productObj=>productObj.percentage))
        if (newPerProductPercentagesSet.size === 1) {
            setGlobalPercentage([...newPerProductPercentagesSet][0])
        } else {
             setGlobalPercentage(null);
        }
        setPayment({
            ...payment,
            per_product_percentages: newPerProductPercentages,
        });
    }, [payment, setPayment, allPayments, setGlobalPercentage, productsMap, products, productDiscounts]);

    const validation = useMemo(() => isSinglePaymentValid(payment), [payment]);

    if (isLoading) {
        return null;
    }

    return (
        <Paper
            sx={{
                padding: 2,
                mb: 4,
                border: 2,
                borderColor: isThisFinalSettlementPayment || validation.isValid ? "success.light" : "error.light",
            }}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} container alignItems="center">
                    <Grid item xs={6}>
                        <Typography>{title}</Typography>
                    </Grid>
                    <Grid item xs={3} container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                            <TextField
                                value={
                                    globalPercentage !== undefined
                                        ? formatDecimalNumber(globalPercentage)
                                        : ""
                                }
                                onChange={(e) => {
                                    if (!e.nativeEvent?.inputType) {
                                        return
                                    }
                                    handleSetGlobalPercentage(Number(e.target.value));
                                }}
                                type="number"
                                inputProps={{ min: 0, max: 100 }}
                                fullWidth
                                label={t("agreements.PaymentCard.percentage")}
                                sx={NUMERIC_FIELDS_DISABLE_ARROW_STYLE}
                                disabled={readonly}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Button onClick={handleSetRestForAll} disabled={readonly || isThisFinalSettlementPayment || isThisReservationPayment}>
                                {t("agreements.PaymentCard.rest")}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={2}>
                        <IconButton onClick={onDelete} disabled={readonly || isThisFinalSettlementPayment}>
                            <DeleteIcon />
                        </IconButton>
                    </Grid>
                </Grid>

                <Grid item xs={12} container spacing={2}>
                    <Grid item xs={4}>
                        <Select
                            value={dueDateType}
                            onChange={(e) => setDueDateType(e.target.value)}
                            fullWidth
                            disabled={readonly}
                        >
                            <MenuItem value="date">
                                {t("agreements.PaymentCard.dueDateTypeDate")}
                            </MenuItem>
                            <MenuItem value="event">
                                {t("agreements.PaymentCard.dueDateTypeEvent")}
                            </MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4}>
                        {dueDateType === "date" ? (
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={language === "pl" ? pl : enUS}
                            >
                                <DatePicker
                                    label={t("agreements.PaymentCard.dueDate")}
                                    value={payment.agrepay_due_date || null}
                                    onChange={handleSetDueDate}
                                    renderInput={(params) => (
                                        <Grid item xs>
                                            <TextField fullWidth {...params} />
                                        </Grid>
                                    )}
                                    disabled={readonly}
                                />
                            </LocalizationProvider>
                        ) : (
                            <Select
                                value={payment.agrepay_milestone}
                                onChange={(e) => handleSetMilestone(e.target.value)}
                                fullWidth
                                disabled={readonly}
                            >
                                {milestones.map((milestone) => (
                                    <MenuItem key={milestone.id} value={milestone.id}>
                                        {milestone.milestone_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        {dueDateType === "event" && (
                            <TextField
                                value={payment.agrepay_payment_shift}
                                onChange={(e) => {
                                    if (!e.nativeEvent?.inputType) {
                                        return
                                    }
                                    handleSetPaymentShift(Number(e.target.value))
                                }}
                                sx={NUMERIC_FIELDS_DISABLE_ARROW_STYLE}
                                label={t("agreements.PaymentCard.dueDateShift")}
                                helperText={t("agreements.PaymentCard.dueDateShiftNote")}
                                type="number"
                                fullWidth
                                disabled={readonly}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <TableContainer>
                        <Table>
                            <StyledTableHead>
                                <TableRow>
                                    <StyledTableCell>
                                        {t("agreements.PaymentCard.product")}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {t("agreements.PaymentCard.grossPrice")}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {t("agreements.PaymentCard.percentage")}
                                    </StyledTableCell>
                                    <StyledTableCell />
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {payment.per_product_percentages.map((pp) => {
                                    const product = productsMap[pp.product_id];
                                    if (!product) {
                                        return null;
                                    }
                                    const discount = productDiscounts?.[pp.product_id] ?? 0;
                                    const pricing = getDiscountedPricesForProduct(
                                        product,
                                        discount,
                                    );
                                    const grossPrice = pricing.gross;
                                    const grossAmount = roundToDecimalPlaces((pp.percentage / 100) * grossPrice, 2)
                                    return (
                                        <TableRow key={pp.product_id}>
                                            <StyledTableCell>{product.name_full}</StyledTableCell>
                                            <StyledTableCell>
                                                <TextField
                                                    value={grossAmount}
                                                    onChange={(e) => {
                                                        if (!e.nativeEvent?.inputType) {
                                                            return
                                                        }
                                                        const value = Number.parseFloat(
                                                            e.target.value,
                                                        );
                                                        if (!Number.isNaN(value)) {
                                                            handleSetGrossAmount(
                                                                pp.product_id,
                                                                value,
                                                            );
                                                        }
                                                    }}
                                                    inputProps={{
                                                        min: isThisFinalSettlementPayment ? -1 * grossPrice.toFixed(2) : 0,
                                                        max: grossPrice.toFixed(2),
                                                        step: 0.01,
                                                    }}
                                                    sx={NUMERIC_FIELDS_DISABLE_ARROW_STYLE}
                                                    type="number"
                                                    fullWidth
                                                    disabled={readonly}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <TextField
                                                    value={formatDecimalNumber(pp.percentage)}
                                                    onChange={(e) => {
                                                        if (!e.nativeEvent?.inputType) {
                                                            return
                                                        }
                                                        handleSetPercentage(
                                                            pp.product_id,
                                                            Number(e.target.value),
                                                        )
                                                                                                            }
                                                    }
                                                    sx={NUMERIC_FIELDS_DISABLE_ARROW_STYLE}
                                                    type="number"
                                                    inputProps={{ min: isThisFinalSettlementPayment ? -100 : 0, max: 100 }}
                                                    fullWidth
                                                    disabled={readonly}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {!isThisReservationPayment && !isFinalSettlementPayment && (
                                                    <Button
                                                        onClick={() =>
                                                            handleSetRest(
                                                                pp.product_id,
                                                                pp.percentage,
                                                            )
                                                        }
                                                        disabled={readonly}
                                                    >
                                                        {t("agreements.PaymentCard.rest")}
                                                    </Button>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    );
                                })}
                               {<TableRow key={"sum"}>
                                    <StyledTableCell sx={{fontWeight: "bold", color:"black"}}>{t("agreements.PaymentCard.total")}</StyledTableCell>
                                    <StyledTableCell>
                                        <TextField
                                            value={
                                                payment.per_product_percentages.reduce((accumulator, pp) => {
                                                const product = productsMap[pp.product_id];
                                                if (!product) {
                                                    return 0;
                                                }
                                                const discount = productDiscounts?.[pp.product_id] ?? 0;
                                                const pricing = getDiscountedPricesForProduct(
                                                    product,
                                                    discount,
                                                );
                                                const grossPrice = pricing.gross || 0;
                                                const grossAmount = Math.round((pp.percentage / 100) * grossPrice * 100) / 100
                                                return ((grossAmount || 0) + parseFloat(accumulator)).toFixed(2)
                                                }, 0)}
                                            type="number"
                                            fullWidth
                                            disabled={false}
                                            onChange={()=>{}}
                                            sx={{...NO_BORDER_TEXT_FILED_STYLE, ...NUMERIC_FIELDS_DISABLE_ARROW_STYLE}}
                                            inputProps ={{style: {fontWeight: "bold", color:"black"}}}
                                            />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <TextField
                                            value={"-"}
                                            fullWidth
                                            disabled={false}
                                            onChange={()=>{}}
                                            sx={NO_BORDER_TEXT_FILED_STYLE}
                                            inputProps ={{style: {fontWeight: "bold", color:"black"}}}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell/>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>
    );
};
